import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, Sun, Moon, Bookmark, Settings, LogIn, LogOut, BarChart3, ShieldCheck, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/lib/theme";
import { useBookmarks } from "@/lib/bookmarks";
import { useAuth } from "@/lib/auth";
import { SCHOLAR_ENABLED } from "@/lib/features";
import { useReviewQueue } from "@/lib/api";
import BrandMark from "@/components/ui/BrandMark";

function SoonBadge() {
  return (
    <span className="ms-1.5 align-middle text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[var(--color-gold)]/15 text-[var(--color-gold)]">
      Soon
    </span>
  );
}

/** Entries awaiting the signed-in scholar's vote, for the Review link's badge. */
function useAwaitingVotes(): number {
  const auth = useAuth();
  const canReview = auth.isScholar && (auth.isAuthenticated || !auth.enabled);
  const queue = useReviewQueue(canReview);
  return canReview ? queue.data?.awaitingMyVote.length ?? 0 : 0;
}

function CountBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="ms-auto min-w-5 h-5 px-1.5 rounded-full bg-[var(--color-gold)] text-[var(--color-midnight)] text-[10px] font-bold flex items-center justify-center" aria-label={`${count} awaiting your vote`}>
      {count}
    </span>
  );
}

/** Sign-in button, or the signed-in user's menu. Hidden in local mode (no Cognito). */
function AccountMenu() {
  const auth = useAuth();
  const awaiting = useAwaitingVotes();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!auth.enabled || auth.isLoading) return null;

  if (!auth.isAuthenticated) {
    return (
      <button
        onClick={auth.signIn}
        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[var(--color-midnight)] bg-[var(--color-gold)] hover:bg-[var(--color-gold)]/90 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" /> Sign in
      </button>
    );
  }

  const initial = (auth.user?.name || auth.user?.email || "?")[0].toUpperCase();
  return (
    <div ref={ref} className="relative hidden md:block">
      <button
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="w-8 h-8 rounded-full bg-[var(--color-gold)]/20 text-[var(--color-gold)] text-sm font-bold hover:bg-[var(--color-gold)]/30 transition-colors"
      >
        {initial}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-xl border border-[var(--color-cream-dark)] py-2 text-sm">
          {auth.user?.email && <p className="px-4 py-2 text-xs text-[var(--color-ink-muted)] truncate">{auth.user.email}</p>}
          {auth.isScholar && (
            <Link role="menuitem" to="/review" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)]">
              <ShieldCheck className="w-4 h-4" /> Scholar review <CountBadge count={awaiting} />
            </Link>
          )}
          {auth.isAdmin && (
            <Link role="menuitem" to="/admin/scholars" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)]">
              <Users className="w-4 h-4" /> Manage scholars
            </Link>
          )}
          {auth.isAdmin && (
            <Link role="menuitem" to="/analytics" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)]">
              <BarChart3 className="w-4 h-4" /> Analytics
            </Link>
          )}
          <button role="menuitem" onClick={auth.signOut} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-[var(--color-cream)] text-left">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** `short` is the desktop label; the mobile menu has room for the full name. */
const navLinks: { to: string; label: string; short: string; soon?: boolean }[] = [
  { to: "/seerah", label: "Seerah Explorer", short: "Seerah" },
  { to: "/timeline", label: "World Timeline", short: "Timeline" },
  { to: "/quran", label: "Quran Companion", short: "Quran" },
  { to: "/clarifier", label: "Misconception Clarifier", short: "Clarifier" },
  { to: "/isnad", label: "Isnad Explorer", short: "Isnad" },
  { to: "/dhikr", label: "Dhikr", short: "Dhikr" },
  { to: "/learning", label: "Learning Paths", short: "Learning" },
  { to: "/scholar", label: "Research Assistant", short: "Assistant", soon: !SCHOLAR_ENABLED },
];

/** True once the page has scrolled past the top few pixels. */
function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

export default function Navbar({ onSearchClick }: { onSearchClick?: () => void }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const scrolled = useScrolled();
  const { toggleTheme, isDark } = useTheme();
  const { bookmarks } = useBookmarks();
  const auth = useAuth();
  const awaiting = useAwaitingVotes();
  // No backdrop-filter while the menu is open: it would become the fixed overlay's containing block.
  const surface = mobileOpen
    ? "bg-[var(--color-night)] border-[var(--color-gold)]/10"
    : scrolled
      ? "bg-[var(--color-night)]/[0.95] backdrop-blur-xl border-[var(--color-gold)]/10"
      : isHome
        ? "bg-transparent border-transparent"
        : "bg-[var(--color-night)] border-[var(--color-gold)]/10";

  // Close the menu on navigation, Escape, or growing to the desktop layout; lock scrolling while it's open.
  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setMobileOpen(false);
    const wide = window.matchMedia("(min-width: 1280px)");
    const resize = () => wide.matches && setMobileOpen(false);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", esc);
    wide.addEventListener("change", resize);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", esc);
      wide.removeEventListener("change", resize);
    };
  }, [mobileOpen]);

  const iconButton =
    "w-10 h-10 rounded-full flex items-center justify-center text-[var(--color-cream)]/60 hover:text-[var(--color-gold)] hover:bg-white/[0.06] transition-colors";
  const mobileRow =
    "flex items-center gap-3 py-3 text-[15px] text-[var(--color-cream)]/70 hover:text-[var(--color-cream)] transition-colors";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${surface}`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between gap-6 h-[72px]">
          <BrandMark />

          {/* Desktop Nav */}
          <div className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((link) => {
              const isActive = location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={isActive ? "page" : undefined}
                  className={`relative px-3 py-2 text-[13.5px] font-medium whitespace-nowrap transition-colors duration-200 ${
                    isActive ? "text-[var(--color-cream)]" : "text-[var(--color-cream)]/60 hover:text-[var(--color-cream)]"
                  }`}
                >
                  {link.short}
                  {link.soon && <SoonBadge />}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute left-1/2 -bottom-0.5 w-1 h-1 -translate-x-1/2 rounded-full bg-[var(--color-gold)]"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1">
            <button
              onClick={onSearchClick}
              className="hidden md:flex items-center gap-2 h-10 ps-3.5 pe-2 me-1 rounded-full bg-white/[0.05] border border-white/10 text-[var(--color-cream)]/50 text-[13px] hover:border-[var(--color-gold)]/40 hover:text-[var(--color-cream)]/80 transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              <span className="pe-6">Search</span>
              <kbd className="text-[10px] px-1.5 py-0.5 rounded-md bg-white/10 text-[var(--color-cream)]/45 font-sans">⌘K</kbd>
            </button>

            <Link
              to="/bookmarks"
              className={`relative ${iconButton}`}
              title="Bookmarks"
              aria-label={`Bookmarks${bookmarks.length ? ` (${bookmarks.length})` : ""}`}
            >
              <Bookmark className="w-4 h-4" />
              {bookmarks.length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[var(--color-gold)] text-[var(--color-midnight)] text-[9px] font-bold flex items-center justify-center">
                  {bookmarks.length}
                </span>
              )}
            </Link>

            <button
              onClick={toggleTheme}
              className={iconButton}
              title={isDark ? "Light mode" : "Dark mode"}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <Link to="/settings" className={`hidden xl:flex ${iconButton}`} title="Settings" aria-label="Settings">
              <Settings className="w-4 h-4" />
            </Link>

            <AccountMenu />

            {/* Mobile menu button */}
            <button
              className={`xl:hidden ${iconButton} !text-[var(--color-cream)]/80`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="xl:hidden fixed inset-x-0 top-[72px] bottom-0 bg-[var(--color-night)] overflow-y-auto"
          >
            <div className="max-w-[1440px] mx-auto px-5 md:px-8 pt-6 pb-[max(2rem,env(safe-area-inset-bottom))]">
              <button
                onClick={() => { setMobileOpen(false); onSearchClick?.(); }}
                className="w-full flex items-center gap-3 h-12 px-4 rounded-full bg-white/[0.05] border border-white/10 text-[15px] text-[var(--color-cream)]/50 mb-6"
              >
                <Search className="w-4 h-4" />
                Search everything
              </button>
              <ul>
                {navLinks.map((link, i) => {
                  const isActive = location.pathname.startsWith(link.to);
                  return (
                    <motion.li
                      key={link.to}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                      className="border-b border-white/[0.07]"
                    >
                      <Link
                        to={link.to}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-baseline justify-between py-3.5 font-display text-[28px] leading-tight tracking-[-0.01em] transition-colors ${
                          isActive ? "text-[var(--color-gold)]" : "text-[var(--color-cream)] hover:text-[var(--color-gold)]"
                        }`}
                      >
                        <span>
                          {link.label}
                          {link.soon && <SoonBadge />}
                        </span>
                        <span className="text-[11px] font-body font-semibold tabular-nums text-[var(--color-cream)]/30">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>

              <div className="mt-8 grid grid-cols-2 gap-x-6">
                <Link to="/bookmarks" onClick={() => setMobileOpen(false)} className={mobileRow}>
                  <Bookmark className="w-4 h-4" />
                  Bookmarks {bookmarks.length > 0 && `(${bookmarks.length})`}
                </Link>
                <Link to="/settings" onClick={() => setMobileOpen(false)} className={mobileRow}>
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                {auth.isScholar && (auth.isAuthenticated || !auth.enabled) && (
                  <Link to="/review" onClick={() => setMobileOpen(false)} className={mobileRow}>
                    <ShieldCheck className="w-4 h-4" />
                    Scholar review
                    <CountBadge count={awaiting} />
                  </Link>
                )}
                {auth.isAdmin && auth.enabled && (
                  <Link to="/analytics" onClick={() => setMobileOpen(false)} className={mobileRow}>
                    <BarChart3 className="w-4 h-4" />
                    Analytics
                  </Link>
                )}
                {auth.enabled && (
                  <button
                    onClick={() => { setMobileOpen(false); if (auth.isAuthenticated) auth.signOut(); else auth.signIn(); }}
                    className={`${mobileRow} !text-[var(--color-gold)]`}
                  >
                    {auth.isAuthenticated ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                    {auth.isAuthenticated ? "Sign out" : "Sign in"}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
